import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Translator from 'translator';
import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core';
import { Refresh as RefreshIcon, Add as AddIcon, RemoveRedEye as ViewIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { FHETable } from 'data/components';
import { Toastr, tools } from 'data/common';
import renderEnvComponent from 'data/common/renderEnvComponent';
import { getProducts, deleteProduct } from '../../data/Products/actions';

class Products extends PureComponent {
    static propTypes = {
        initialized: PropTypes.bool.isRequired,
        products: PropTypes.object.isRequired,
        homes: PropTypes.object.isRequired,
        getProducts: PropTypes.func.isRequired,
        profile: PropTypes.object.isRequired,
        classes: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        deleteProduct: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);
        const { initialized } = props;
        this.state = {
            loading: !initialized
        };
    }

    componentDidMount() {
        const { initialized } = this.props;
        if (!initialized) this.refreshProducts();
    }

    refreshProducts = () => {
        const { getProducts } = this.props;
        this.setState({ loading: true });
        return getProducts()
            .catch(err => {
                const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                Toastr.error(message);
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    };

    deletingProduct = (productType, identifier, type, homeId, callbackRowActionLoading) => {
        const { deleteProduct } = this.props;
        deleteProduct(homeId, productType, identifier, type)
            .then(() => {
                Toastr.success(Translator.translate(`generic.products.actions.deleteProductSuccess`));
            })
            .catch(err => {
                const message = Translator.translateWithFallback(
                    `api.message.${err.message ? err.message : err}`,
                    'generic.products.actions.deleteProductError'
                );
                Toastr.error(message);
            })
            .finally(() => {
                callbackRowActionLoading(false);
            });
    };

    navigateToDetail = rowData => {
        const { history } = this.props;
        return history.push({
            pathname: `products/${rowData.homeId}/${rowData.productType}/${rowData.productIdentifier}`
        });
    };
    navigateToHomeDetail = rowData => {
        const { history } = this.props;
        return history.push({ pathname: `home/detail/${rowData.homeId}` });
    };

    navigateToAdd = () => {
        const { history } = this.props;
        return history.push({ pathname: 'products/add' });
    };

    hasRightToDetail = rowData => {
        const { profile, history } = this.props;
        return (
            rowData.state !== Translator.translate('general.dissociated') &&
            (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes(`products_${rowData.productType}_details`))
        );
    };

    hasRightToDelete = rowData => {
        const { profile, history } = this.props;

        return (
            rowData.state === Translator.translate('general.dissociated') &&
            (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes(`products_${rowData.productType}_delete`))
        );
    };

    hideDeleteButton = rowData => {
        const { profile, history } = this.props;
        const deletableProductType = ['fhe_product', 'airzone'];

        return (
            deletableProductType.includes(rowData.productType) &&
            (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes(`products_${rowData.productType}_delete`))
        );
    };

    deleteProduct = (rowData, callbackRowActionLoading) => {
        callbackRowActionLoading(true);
        Toastr.confirm(
            Translator.translate('generic.products.actions.confirmationDeleteProduct', rowData.label),
            Translator.translate('generic.products.actions.confirmationDeleteProductTitle'),
            {
                onYes: () => {
                    this.deletingProduct(rowData.productType, rowData.productIdentifier, rowData.type, rowData.homeId, callbackRowActionLoading);
                },
                onNo: () => callbackRowActionLoading(false)
            }
        );
    };

    renderActions = memoizeOne(profile => {
        const { history } = this.props;
        let actions = {
            row: [],
            toolbar: [
                {
                    icon: RefreshIcon,
                    tooltip: Translator.translate('generic.products.table.globalAction.refresh.tooltip'),
                    onClick: this.refreshProducts
                }
            ]
        };
        const userRights = profile.getIn(['user_rights', history.environment], []);
        if (profile.get('admin') || userRights.includes('addProduct')) {
            actions.toolbar.push({
                icon: AddIcon,
                tooltip: Translator.translate(`generic.products.table.globalAction.add.tooltip`),
                onClick: this.navigateToAdd
            });
        }
        if (profile.get('admin') || userRights.includes('productsDetails')) {
            actions.row.push({
                icon: ViewIcon,
                tooltip: Translator.translate(`general.showDetail`),
                hasRight: this.hasRightToDetail,
                onClick: this.navigateToDetail
            });
        }

        actions.row.push({
            icon: DeleteIcon,
            hasRight: this.hasRightToDelete,
            isHidden: this.hideDeleteButton,
            onClick: (rowData, callbackRowActionLoading) => {
                this.deleteProduct(rowData, callbackRowActionLoading);
            },
            tooltip: Translator.translate(`generic.products.actions.confirmationDeleteProductTitle`)
        });

        return actions;
    });

    renderColumns = memoizeOne(profile => {
        const { classes, history } = this.props;
        const userRights = profile.getIn(['user_rights', history.environment], []);

        return [
            {
                title: Translator.translate('general.table.column.identifier'),
                name: 'identifier',
                width: 150,
                customFilter: (value, filter) => {
                    if (!value) return false;
                    const formattedValue = value.toLowerCase().replace(/:/g, '');
                    const formattedFilter = filter.value.toLowerCase().replace(/:/g, '');
                    return formattedValue.indexOf(formattedFilter) !== -1;
                }
            },
            {
                title: Translator.translate('general.table.column.homeLabel'),
                name: 'homeLabel',
                action: (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes('homeDetails')) && {
                    onClick: this.navigateToHomeDetail,
                    tooltip: Translator.translate(`general.showHome`)
                }
            },
            {
                title: Translator.translate('general.table.column.type'),
                name: 'type',
                action: (profile.get('admin') || userRights.includes('productsDetails')) && {
                    onClick: this.navigateToDetail,
                    hasRight: this.hasRightToDetail,
                    tooltip: Translator.translate(`general.showDetail`)
                }
            },
            {
                title: Translator.translate('general.table.column.label'),
                name: 'label'
            },
            {
                title: Translator.translate('general.table.column.version'),
                name: 'version'
            },

            {
                title: Translator.translate('general.table.column.status'),
                name: 'state',
                render: (row, data, value) => {
                    if (!value) return null;
                    return (
                        <span className={value === Translator.translate('general.associated') ? classes.associated : classes.dissociated}>
                            {value}
                        </span>
                    );
                }
            },
            {
                title: Translator.translate('general.table.column.state'),
                name: 'status',
                render: (row, data, value) => {
                    if (!value) return null;
                    return (
                        <span className={value === Translator.translate('general.connected') ? classes.connected : classes.disconnected}>
                            {value}
                        </span>
                    );
                }
            }
        ];
    });

    renderData = memoizeOne((products, homes) => {
        let result = [];

        products.map((product, productkey) => {
            result.push({
                key: productkey,
                identifier: product
                    .get('identifier')
                    .match(/.{1,2}/g)
                    .join(':'),
                productIdentifier: product.get('identifier'),
                productType: product.get('productType'),
                homeLabel: homes.getIn([product.get('homeId').toString(), 'label'], ''),
                homeId: parseInt(product.get('homeId')),
                type: product.get('productType') === 'fhe_product' ? product.get('type') : Translator.translate(`general.${product.get('type')}`),
                label: product.getIn(['label'], ''),
                version: product.getIn(['version'], ''),
                state: product.getIn(['state'], '') === '' ? '' : Translator.translate(`general.${product.get('state')}`),
                status: product.getIn(['status'], '') === '' ? '' : Translator.translate(`general.${product.get('status')}`)
            });
        });

        return result;
    });

    render() {
        const { loading } = this.state;
        const { products, homes, profile, history } = this.props;

        return (
            <FHETable
                idTable="productList"
                columns={this.renderColumns(profile)}
                data={this.renderData(products, homes)}
                actions={this.renderActions(profile)}
                title={Translator.translate(`generic.products.table.title`)}
                emptyDataSourceMessage={Translator.translate(`generic.products.table.body.emptyDataSourceMessage`)}
                isLoading={loading}
            />
        );
    }
}

function mapStateToProps(state, props) {
    const { history } = props;
    return {
        profile: state.data.user,
        products: state.environments[history.environment].data.Products.get('list'),
        initialized: state.environments[history.environment].data.Products.get('initialized'),
        homes: state.environments[history.environment].data.Homes.get('list')
    };
}

const styles = theme => ({
    connected: {
        color: theme.palette.success.main
    },
    disconnected: {
        color: theme.palette.error.main
    },
    associated: {
        color: theme.palette.success.main
    },
    dissociated: {
        color: theme.palette.error.main
    }
});

export default withRouter(
    renderEnvComponent(
        'scenes/Products/index',
        connect(
            mapStateToProps,
            (dispatch, ownProps) =>
                bindActionCreators(tools.mergePropsToAction(ownProps.history.environment, { getProducts, deleteProduct }), dispatch)
        )(withStyles(styles, { withTheme: true })(Products))
    )
);



// WEBPACK FOOTER //
// ./environments/genericEnv/scenes/Products/index.js